/**
 * Shows/hides prev/next arrow buttons over a horizontally-scrollable group
 * (see `scroll_horizontal_mobile` group setting), and scrolls one "page" on click.
 * The right arrow appears only if there's more content to the right, and the
 * left arrow only once the user has scrolled away from the start.
 */
class ScrollNavArrows extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scrollEl = null;

  /** @type {HTMLButtonElement | null} */
  #prevButton = null;

  /** @type {HTMLButtonElement | null} */
  #nextButton = null;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    const groupBlock = this.closest('.group-block');
    this.#scrollEl = groupBlock?.querySelector(':scope > .layout-panel-flex--scroll-mobile') ?? null;
    if (!this.#scrollEl) return;

    this.#prevButton = this.querySelector('[data-direction="prev"]');
    this.#nextButton = this.querySelector('[data-direction="next"]');
    if (!this.#prevButton || !this.#nextButton) return;

    this.#prevButton.addEventListener('click', this.#handlePrevClick);
    this.#nextButton.addEventListener('click', this.#handleNextClick);
    this.#scrollEl.addEventListener('scroll', this.#handleScroll, { passive: true });

    this.#resizeObserver = new ResizeObserver(this.#handleScroll);
    this.#resizeObserver.observe(this.#scrollEl);

    this.#handleScroll();
  }

  disconnectedCallback() {
    this.#scrollEl?.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver?.disconnect();
  }

  /** @param {MouseEvent} event */
  #handlePrevClick = (event) => {
    event.preventDefault();
    this.#scrollEl?.scrollBy({ left: -this.#pageWidth, behavior: 'smooth' });
  };

  /** @param {MouseEvent} event */
  #handleNextClick = (event) => {
    event.preventDefault();
    this.#scrollEl?.scrollBy({ left: this.#pageWidth, behavior: 'smooth' });
  };

  get #pageWidth() {
    return (this.#scrollEl?.clientWidth ?? 0) * 0.9;
  }

  #handleScroll = () => {
    const scrollEl = this.#scrollEl;
    if (!scrollEl || !this.#prevButton || !this.#nextButton) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollEl;
    const maxScrollLeft = scrollWidth - clientWidth;
    const epsilon = 2;

    this.#prevButton.hidden = scrollLeft <= epsilon;
    this.#nextButton.hidden = maxScrollLeft <= epsilon || scrollLeft >= maxScrollLeft - epsilon;
  };
}

if (!customElements.get('scroll-nav-arrows')) {
  customElements.define('scroll-nav-arrows', ScrollNavArrows);
}
